import { cn } from "@/lib/utils";
import { formatHours } from "@/lib/format";

interface Props {
  budgetHours: number;
  assignedHours: number;
  /** Time-entries på linjen som ikke matcher noen assignments (sum av timer). */
  looseEntryHours?: number;
  /** Antall løse time_entries (count, ikke timer). */
  looseEntryCount?: number;
}

/**
 * Kombinert fordelings-pille: viser om PL har tildelt timene til personer
 * (fordelt/delvis/ikke fordelt), og eskalerer med rød suffiks når det
 * finnes timeføringer som ikke har et hjem (løse timer).
 *
 * Erstatter de tre separate badgesene + den røde "N løse"-bobla med ett
 * skannbart signal per oppgave.
 */
export function BudgetFordelingPill({
  budgetHours,
  assignedHours,
  looseEntryHours = 0,
  looseEntryCount = 0,
}: Props) {
  const fordeling = (() => {
    if (budgetHours === 0) return "tom" as const;
    if (assignedHours >= budgetHours) return "fordelt" as const;
    if (assignedHours > 0) return "delvis" as const;
    return "ikke" as const;
  })();

  const labels = {
    fordelt: "Fordelt",
    delvis: "Delvis fordelt",
    ikke: "Ikke fordelt",
    tom: "—",
  } as const;

  // Dot-farge per fordelingsstatus.
  const dotClass = {
    fordelt: "bg-emerald-500",
    delvis: "bg-amber-500",
    ikke: "bg-muted-foreground/40",
    tom: "bg-muted-foreground/40",
  }[fordeling];

  const hasLoose = looseEntryCount > 0 && looseEntryHours > 0;

  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-muted/50 border border-foreground/10 text-[11px] font-medium text-muted-foreground whitespace-nowrap">
      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", dotClass)} />
      {labels[fordeling]}
      {hasLoose && (
        <>
          <span className="text-muted-foreground/50">·</span>
          <span className="inline-flex items-center gap-1 text-destructive font-semibold">
            <span className="w-1 h-1 rounded-full bg-destructive shrink-0" />
            {formatHours(looseEntryHours)} løse
          </span>
        </>
      )}
    </span>
  );
}
